import { Toast } from "@skbkontur/react-ui";

import {
    FormSaveStatusAction,
    FormSaveStatusActionType,
    FormSavingStatus,
} from "../../Controls/FormParts/Panel/FormSaveStatus/Store/FormSaveStatusActions";
import {
    formCloseEvent,
    formInterdocumentTransferEvent,
    formPrintEvent,
    formSaveEvent,
    formSendEvent,
    formSendToClientForSign,
    formShowFeedbackEvent,
    formShowTourEvent,
} from "../Core/EventManagement/ExternalEvents/ExternalEvents";
import { CandyThunkAction } from "../Core/Store/CandyReduxTypes";
import { clearAllFiltersSessions } from "../../Controls/MultiControls/MultiLine/Store/FiltersSessionHelpers";
import { FormAction, FormActionType } from "../Form/Store/FormActions";
import { ActionPanelActionType } from "../../Controls/FormParts/Panel/ActionPanel/Store/ActionPanelActions";
import { UIUtil } from "../Form/CustomizationContexts/HelperFunctionContext/UIUtil/UIUtil";
import { track } from "../../Controls/Internals/Metrics/MetricsProvider";

export const formSaveAction2: CandyThunkAction<Promise<void>> = async (_, __, form) => {
    try {
        await form.changeSet.send();
        form.triggerExternalEvent(formSaveEvent());
    } catch {
        Toast.push("Не удалось сохранить данные. Попробуйте еще раз.");
    }
};

export const formSaveAction: CandyThunkAction = (_, __, form) => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    form.changeSet.send({ force: true, operationName: undefined });
};

export const formSilentSaveAction: CandyThunkAction<Promise<void>> = (_, __, form) => form.changeSet.send();

export const formSendAction: CandyThunkAction<Promise<void>> = async (_, __, form) => {
    try {
        await form.changeSet.send();
        form.triggerExternalEvent(formSendEvent());
    } catch {
        track("candy", "hasErrorOnSend");
        // eslint-disable-next-line no-alert
        if (confirm("Не удалось сохранить последние изменения. Перейти к отправке формы?")) {
            track("candy", "sendWithErrors");
            form.triggerExternalEvent(formSendEvent());
        }
    }
};

export const formCloseAction: CandyThunkAction<Promise<void>> = async (_, __, form) => {
    await form.changeSet.send();
    form.triggerExternalEvent(formCloseEvent());
};

export const formSendToClientForSignAction: CandyThunkAction<Promise<void>> = async (_, __, form) => {
    form.triggerExternalEvent(formSendToClientForSign());
};

export const feedbackHandler: CandyThunkAction = (_, __, form): void => {
    form.triggerExternalEvent(formShowFeedbackEvent());
};

export const clearFormHandler: CandyThunkAction<Promise<void>> = async (_, __, form): Promise<void> => {
    const uiUtil = new UIUtil(form.settings.theme);
    const permission = await uiUtil.confirm(
        "После очистки все данные будут удалены",
        "Очистить",
        undefined,
        "Очистить форму?",
        false,
        "TrashCanIcon",
        "danger"
    );

    if (!permission) {
        return;
    }

    try {
        clearAllFiltersSessions();
        await form.changeSet.send();
        await form.candyApiDraftClient.clearDraft();
        window.location.reload();
    } catch {
        Toast.push("Не удалось очистить форму. Попробуйте позже.");
    }
};

export const restoreFormToSourceFuf: CandyThunkAction<Promise<void>> = async (_, __, form): Promise<void> => {
    const uiUtil = new UIUtil(form.settings.theme);
    const permission = await uiUtil.confirm(
        "Все внесенные изменения будут удалены",
        "Вернуть данные",
        undefined,
        "Вернуть исходные данные?",
        false,
        "DocArrowInIcon"
    );

    if (!permission) {
        return;
    }

    try {
        await uiUtil.executeLongOperation(
            {
                allowCancel: false,
                title: "Возвращаем исходные данные",
                modalSpecialCase: "InfiniteProcess",
            },
            async () => {
                await form.changeSet.send();
                await form.candyApiDraftClient.restoreToSourceFuf();
                await form.reloadInner();
            }
        );
    } catch {
        Toast.push("Не удалось вернуть исходные данные. Попробуйте позже.");
    }
};

export const fufSaveHandler: CandyThunkAction<Promise<void>> = async (dispatch, _, form): Promise<void> => {
    dispatch({ type: ActionPanelActionType.BeginDownloadFuf });
    await form.changeSet.send();
    await form.normalizeDirtyMasksAndReloadInnerIfRequired();
    await form.candyApiDraftClient.downloadFuf();
    dispatch({ type: ActionPanelActionType.EndDownloadFuf });
};

export const innerSaveHandler: CandyThunkAction<Promise<void>> = async (dispatch, _, form): Promise<void> => {
    dispatch({ type: ActionPanelActionType.BeginDownloadInner });
    await form.changeSet.send();
    await form.normalizeDirtyMasksAndReloadInnerIfRequired();
    await form.candyApiDraftClient.downloadInner();
    dispatch({ type: ActionPanelActionType.EndDownloadInner });
};

export const printHandler: CandyThunkAction<Promise<void>> = async (_, __, form): Promise<void> => {
    await form.changeSet.send();
    await form.normalizeDirtyMasksAndReloadInnerIfRequired();
    form.triggerExternalEvent(formPrintEvent());
};

export const enableAutoValuesHandler: CandyThunkAction<Promise<void>> = async (_, __, form): Promise<void> => {
    await form.customNormalizationHelper.fillAutoFieldsWithAutoValuesAndReload([], true);
};

export const showTourHandler: CandyThunkAction = (_, __, form): void => {
    form.triggerExternalEvent(formShowTourEvent());
};

export const interdocumentTransferHandler: CandyThunkAction = (_, __, form): void => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    form.changeSet.send().then(() => form.triggerExternalEvent(formInterdocumentTransferEvent()));
};

export const convertDraftFromPrevGfvAction: CandyThunkAction<Promise<void>> = async (_, __, form) => {
    await form.changeSet.send();
    await form.normalizeDirtyMasksAndReloadInnerIfRequired();
    await form.candyApiDraftClient.convertDraftFromPrevGfv({
        // @ts-ignore Я хз, кажется prevGfvDraftId должен быть undefined -- надо вставить проверку и кинуть Error, но я не увереын
        prevGfvDraftId: form.settings.prevGfvDraftId,
        gfv: form.settings.gfv,
    });

    window.location.reload();
};

export function setFormSaveStatus(status: FormSavingStatus): FormSaveStatusAction {
    return { type: FormSaveStatusActionType.SetSavingStatus, payload: { status: status } };
}

export function setFormReloading(isFormReloading: boolean): FormAction {
    return { type: FormActionType.SetFormReloading, payload: { isFormReloading: isFormReloading } };
}
